import { Box, CircularProgress, Typography, alpha } from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import { SideDrawer } from "@/components/common/SideDrawer";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ValidAny } from "@/types";
import { CellHighlightMap } from "../../utils/sourcePreviewUtils";
import { SpreadsheetPreview } from "./SpreadsheetPreview";

// =============================================================================
// Types
// =============================================================================

interface SourcePreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  sheetData: ValidAny[][] | null;
  highlightsFor: (startRow: number, endRow: number) => CellHighlightMap;
  sheetName: string | null;
  headerRowIndex: number;
  isLoading: boolean;
  error: string | null;
  /** Row to scroll into view (0-indexed sheet row) */
  focusRow: number | null;
  /** True when a template was expected but couldn't be loaded */
  templateUnavailable?: boolean;
  /**
   * The highest sheet row the highlight index covers, or null for none
   * (#6900 part B).
   *
   * Highlights are built from the LOADED records, so on a paged document they
   * stop partway down the sheet. Past that row every cell renders
   * unhighlighted, which reads as "nothing was extracted from these rows"
   * rather than "these records are not loaded". Blank cells must never imply
   * the extraction found nothing (#6900 AC2), so the drawer says which it is.
   */
  highlightedThroughRow?: number | null;
  /** True when more records exist than are loaded. */
  hasMoreRecords?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export const SourcePreviewDrawer = ({
  open,
  onClose,
  sheetData,
  highlightsFor,
  sheetName,
  headerRowIndex,
  isLoading,
  error,
  focusRow,
  templateUnavailable,
  highlightedThroughRow,
  hasMoreRecords,
}: SourcePreviewDrawerProps) => {
  const { theme } = useAppTheme();
  const title = sheetName ? `Source: ${sheetName}` : "Source Preview";

  return (
    <SideDrawer title={title} width={650} open={open} onClose={onClose} hideHandle>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 1.5 }}>
        <HighlightLegend />

        {templateUnavailable && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: 1,
              borderColor: alpha(theme.palette.info.main, 0.2),
            }}
          >
            <InfoIcon sx={{ fontSize: 16, color: "info.main", mt: 0.25 }} />
            <Typography variant="caption" color="text.secondary">
              Field mapping template unavailable — some extraction issues may not be highlighted.
            </Typography>
          </Box>
        )}

        {isLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading spreadsheet...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          </Box>
        )}

        {/* Highlights cover only the loaded records (#6900). Stated rather
            than left to inference: an unhighlighted row looks exactly like a
            row nothing was extracted from, and on a claims review surface that
            is a false negative about the extraction. Only shown when the
            coverage actually stops short — on a fully loaded document the
            highlights are complete and this would be noise. */}
        {!isLoading && !error && sheetData && hasMoreRecords && typeof highlightedThroughRow === "number" && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Highlights cover rows up to {(highlightedThroughRow + 1).toLocaleString()} — the records loaded so far. Rows
            below that are not highlighted because their records are not loaded, not because nothing was extracted from
            them.
          </Typography>
        )}

        {!isLoading && !error && sheetData && (
          <SpreadsheetPreview
            data={sheetData}
            highlightsFor={highlightsFor}
            focusRow={focusRow}
            headerRowIndex={headerRowIndex}
          />
        )}
      </Box>
    </SideDrawer>
  );
};

// =============================================================================
// Legend
// =============================================================================

const HighlightLegend = () => {
  const { theme, isDarkMode } = useAppTheme();

  const items = [
    { label: "Extracted", color: theme.palette.success.main },
    { label: "Error", color: theme.palette.error.main },
    { label: "Warning", color: theme.palette.warning.main },
  ];

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      {items.map(({ label, color }) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.5,
              bgcolor: alpha(color, isDarkMode ? 0.3 : 0.2),
              border: 1,
              borderColor: alpha(color, 0.4),
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
